import React, { useMemo } from "react";
import { Button } from "react-bootstrap";
// Importing table utilities
import PaginationControls from "./pagination/PaginationControls";
// Importing icons
import { BsPeople } from "react-icons/bs";
import { FiEdit } from "react-icons/fi";
// other imports
import { translateGender } from "../../../helper/surveyUtils";
// new table
import Table from "./Table";
import TableEditDropdownButton from "./TableEditDropdownButton";
import { BsFillCircleFill, BsCircle } from "react-icons/bs";
import { Patient } from "../../../services/getFunctions";
import {
  BsFileEarmarkX,
  BsFileEarmarkMinus,
  BsFileEarmarkCheck,
} from "react-icons/bs";
import TableHeaderControls from "./pagination/TableHeaderControls";
import {
  FilterState,
  SortField,
} from "../../../helper/Hooks/usePatientTableState";

interface PaginationState {
  currentPage: number;
  totalCount: number;
  totalPages: number;
  pageSize: number;
}

/* Component for PatientTable */
interface PatientTableProps {
  patients: Patient[];
  userType: string;
  openEditPatientModal: (event) => void;
  openAssignClinicianModal: (event) => void;
  openSurveyStateModal: (event) => void;
  openResultModal: (event) => void;
  pagination: PaginationState;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  updateStringFilter: (field: keyof FilterState, value?: string) => void;
  updateSelectFilter: (field: keyof FilterState, value?: string) => void;
  updateDateFilter: (
    field: "date_of_birth" | "admission" | "discharge",
    lessThan?: string,
    greaterThan?: string,
  ) => void;
  updateLabelFilter: (value?: string) => void;
  updateReportStateFilter: (value?: string) => void;
  updateSort: (sort?: SortField, order?: "ASC" | "DESC") => void;
}

/* Component for EditDropdown in the edit column */
interface EditDropdownProps {
  index: number;
  modalID: string;
  patient: Patient;
  openEditPatientModal: (event: React.MouseEvent<HTMLButtonElement>) => void;
  openAssignClinicianModal: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void;
}

/**
 * Defining what the edit-dropdown in this table should do
 */
const EditDropdown = ({
  index,
  modalID,
  openEditPatientModal,
  openAssignClinicianModal,
}: EditDropdownProps) => {
  const dropdownItems = [
    {
      key: "0",
      text: (
        <>
          <FiEdit /> Patient*in bearbeiten
        </>
      ),
      onClick: openEditPatientModal,
    },
    {
      key: "1",
      text: (
        <>
          <BsPeople /> Anderen Mitarbeitenden zuweisen
        </>
      ),
      onClick: openAssignClinicianModal,
    },
  ];

  return (
    <TableEditDropdownButton
      index={index}
      modalID={modalID}
      items={dropdownItems}
    />
  );
};

/**
 * Displays a table of clinicians with various functionalities such as sorting, filtering, and pagination.
 */
const PatientTable = ({
  patients,
  userType,
  openEditPatientModal,
  openAssignClinicianModal,
  openSurveyStateModal,
  openResultModal,
  pagination,
  onPageChange,
  onPageSizeChange,
  updateStringFilter,
  updateDateFilter,
  updateSelectFilter,
  updateLabelFilter,
  updateReportStateFilter,
  updateSort,
}: PatientTableProps) => {
  // Define table columns (order is important!)
  const columns = useMemo(() => {
    const edit = {
      accessorKey: "edit",
      header: "",
      customCSS: { width: "70px" },
    };

    const code = {
      accessorKey: "code",
      header: "SysDok-ID",
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        sortField: "CODE",
        filterVariant: "serverString",
        onStringFilterChange: (value?: string) => {
          updateStringFilter("code_like", value);
        },
        onSortChange: (field: SortField, order: "ASC" | "DESC") => {
          updateSort(field, order);
        },
      },
    };

    const clinician = {
      accessorKey: "clinician_username",
      header: "Mitarbeitende",
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        clearServerSort: () => updateSort(),
        filterVariant: "serverString",
        onStringFilterChange: (value?: string) => {
          updateStringFilter("clinician_username_like", value);
        },
      },
    };

    const dateOfBirth = {
      accessorKey: "date_of_birth",
      header: "Geburtsdatum",
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        sortField: "DATE_OF_BIRTH",
        filterVariant: "serverDate",
        onDateFilterChange: (lessThan?: string, greaterThan?: string) => {
          updateDateFilter("date_of_birth", lessThan, greaterThan);
        },
        onSortChange: (field: SortField, order: "ASC" | "DESC") => {
          updateSort(field, order);
        },
      },
    };

    const gender = {
      accessorKey: "gender",
      header: "Geschlecht",
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        sortField: "GENDER",
        filterVariant: "serverSelect",
        filterOptions: ["MALE", "FEMALE", "DIVERSE"],
        onSelectFilterChange: (value?: string) => {
          updateSelectFilter("gender_equals", value);
        },
        onSortChange: (field: SortField, order: "ASC" | "DESC") => {
          updateSort(field, order);
        },
      },
    };

    const admission = {
      accessorKey: "admission",
      header: "Aufnahmedatum",
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        sortField: "ADMISSION",
        filterVariant: "serverDate",
        onDateFilterChange: (lessThan?: string, greaterThan?: string) => {
          updateDateFilter("admission", lessThan, greaterThan);
        },
        onSortChange: (field: SortField, order: "ASC" | "DESC") => {
          updateSort(field, order);
        },
      },
    };

    const discharge = {
      accessorKey: "discharge",
      header: "Entlassdatum",
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        sortField: "DISCHARGE",
        filterVariant: "serverDate",
        onDateFilterChange: (lessThan?: string, greaterThan?: string) => {
          updateDateFilter("discharge", lessThan, greaterThan);
        },
        onSortChange: (field: SortField, order: "ASC" | "DESC") => {
          updateSort(field, order);
        },
      },
    };

    const labels = {
      accessorKey: "labelsText",
      header: "Labels",
      enableColumnFilter: true,
      meta: {
        filterVariant: "serverString",
        onStringFilterChange: (value?: string) => {
          updateLabelFilter(value);
        },
      },
    };

    const therapyEnd = {
      accessorKey: "therapyEnd",
      header: "Therapie",
    };

    const status = {
      accessorKey: "state",
      header: "Befragungsstatus",
    };

    const resultAndReport = {
      accessorKey: "resultAndReport",
      header: "Ergebnis",
      enableColumnFilter: true,
      meta: {
        filterVariant: "serverSelect",
        filterOptions: ["NO_REPORT", "UNREAD_REPORT", "ALL_REPORT_READ"],
        onSelectFilterChange: (value?: string) => {
          updateReportStateFilter(value);
        },
      },
    };

    if (userType === "clinician") {
      return [
        edit,
        code,
        dateOfBirth,
        gender,
        admission,
        discharge,
        labels,
        therapyEnd,
        status,
        resultAndReport,
      ];
    }
    if (userType === "admin") {
      return [
        edit,
        code,
        clinician,
        dateOfBirth,
        gender,
        admission,
        discharge,
        labels,
        therapyEnd,
        status,
      ];
    }
    return [];
  }, [
    userType,
    updateSort,
    updateLabelFilter,
    updateDateFilter,
    updateStringFilter,
    updateSelectFilter,
    updateReportStateFilter,
  ]);

  // Prepare data for the table rows (order is important)
  const data = useMemo(
    () =>
      patients.map((patient, index) => {
        const edit = (
          <EditDropdown
            {...{
              index,
              modalID: patient.code,
              patient,
              openEditPatientModal,
              openAssignClinicianModal,
            }}
          />
        );

        /**
         * define the labels (system-id in table)
         * contains elements and primitive string for filtering
         */
        const labelData = () => {
          const filteredLabels = patient.labels.filter(
            (label) => label.key === "CLUSTER" && label.value,
          );
          const searchText = filteredLabels.map((item) => item.value).join(" ");

          const labelElements = (
            <div
              style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}
              data-search-text={searchText}
            >
              {filteredLabels.map((item, idx) => (
                <span
                  key={idx}
                  style={{
                    backgroundColor: "#e3f2fd",
                    color: "#1565c0",
                    border: "1px solid #bbccdfff",
                    padding: "2px 5px",
                    borderRadius: "4px",
                    fontSize: "0.9em",
                    display: "inline-block",
                  }}
                  data-label={item.value}
                >
                  {item.value}
                </span>
              ))}
            </div>
          );

          return {
            element: labelElements,
            searchText: searchText,
          };
        };

        //get the end session info
        const endReasonLabel = patient.labels.find(
          (label) => label.key === "THERAPY_END_REASON",
        );
        const sessionCountLabel = patient.labels.find(
          (label) => label.key === "THERAPY_END_SESSION_COUNT",
        );

        const hasEndReason = endReasonLabel && endReasonLabel.value;
        const hasSessionCount = sessionCountLabel && sessionCountLabel.value;

        //map the ui language for end reason
        const formatTherapyEndReason = (reason?: string) => {
          if (!reason) return "";
          const reasonMap = {
            RESUMPTION: "Wiederaufnahme",
            REGULAR_ENDING: "Reguläres Ende",
            EARLY_TERMINATION_BY_MUTUAL_AGREEMENT: "Einvernehmlich beendet",
            TERMINATION_BY_PATIENT: "Abbruch durch Patient*in",
            TERMINATION_BY_THERAPIST: "Abbruch durch Therapeut*in",
            OTHER: "Sonstiger Grund",
          };
          return reasonMap[reason] || reason;
        };

        // tooltip text
        const getTooltipText = () => {
          if (hasEndReason && hasSessionCount) {
            const reasonText = formatTherapyEndReason(endReasonLabel?.value);
            const countText = sessionCountLabel?.value || "";
            return `${reasonText}\nSitzungen: ${countText}`;
          }
          return "nicht dokumentiert";
        };

        const patientLabels = (
          <div
            style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
          >
            {hasEndReason && hasSessionCount ? (
              <span
                role="img"
                aria-label={getTooltipText()}
                title={getTooltipText()}
                style={{
                  color: "#000000",
                  marginRight: "8px",
                  cursor: "help",
                  fontSize: "1.1em",
                }}
              >
                <BsFillCircleFill aria-hidden="true" />
              </span>
            ) : (
              <span
                role="img"
                aria-label="nicht dokumentiert"
                title="nicht dokumentiert"
                style={{
                  color: "#6c757d",
                  marginRight: "8px",
                  cursor: "help",
                  fontSize: "1.1em",
                }}
              >
                <BsCircle aria-hidden="true" />
              </span>
            )}
          </div>
        );

        const StyledButton = ({ id, onClick, children }) => (
          <Button
            variant="outline-primary"
            size="sm"
            className="px-3"
            style={{
              fontSize: "0.9rem",
              borderRadius: "20px",
              backgroundColor: "transparent",
              color: "#16549bff",
              border: "1px solid #bbccdfff",
              fontWeight: "500",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#2a5e99ff";
              e.currentTarget.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "#1565c0";
            }}
            id={id}
            onClick={onClick}
          >
            {children}
          </Button>
        );

        const surveyState = (
          <StyledButton id={patient.id} onClick={openSurveyStateModal}>
            Anzeigen
          </StyledButton>
        );

        //define icos and text for each report status
        const reportStateMap = {
          NO_REPORT: {
            icon: <BsFileEarmarkX />,
            tooltip: "Nicht verfügbar",
          },
          UNREAD_REPORT: {
            icon: <BsFileEarmarkMinus />,
            tooltip: "Ungelesener Report",
          },
          ALL_REPORT_READ: {
            icon: <BsFileEarmarkCheck />,
            tooltip: "Alle Reports gelesen",
          },
        };

        const reportState = patient.report_state ?? "NO_REPORT";
        const reportStats = reportStateMap[reportState];

        const resultAndReport = (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "2px",
            }}
          >
            {/* button for showing results */}
            <StyledButton id={patient.id} onClick={openResultModal}>
              Anzeigen
            </StyledButton>
            {/* text to display readed reports */}
            <div
              style={{
                fontSize: "0.875em",
                color: "#1565c0",
                display: "inline-flex",
                alignItems: "center",
                padding: "0.3px 9px",
                backgroundColor: "#e3f2fd",
                borderRadius: "14px",
                fontWeight: "500",
                alignSelf: "flex-end",
              }}
              title={reportStats.tooltip}
            >
              <span
                style={{
                  marginRight: "4px",
                  lineHeight: "1",
                  fontSize: "1.1em",
                }}
              >
                {reportStats.icon}
              </span>
            </div>
          </div>
        );

        let data = {};

        if (userType === "clinician") {
          data = {
            edit: edit,
            code: patient.code,
            date_of_birth: patient.date_of_birth,
            gender: translateGender(patient.gender),
            admission: patient.admission,
            discharge: patient.discharge,
            labelsText: labelData().element,
            therapyEnd: patientLabels,
            state: surveyState,
            resultAndReport: resultAndReport,
          };
        } else if (userType === "admin") {
          data = {
            edit: edit,
            code: patient.code,
            clinician_username: patient.clinician_username,
            date_of_birth: patient.date_of_birth,
            gender: translateGender(patient.gender),
            admission: patient.admission,
            discharge: patient.discharge,
            labelsText: labelData().element,
            therapyEnd: patientLabels,
            state: surveyState,
          };
        }
        return data;
      }),
    [
      patients,
      userType,
      openAssignClinicianModal,
      openEditPatientModal,
      openResultModal,
      openSurveyStateModal,
    ],
  );

  return (
    <>
      <TableHeaderControls
        totalCount={pagination.totalCount}
        currentPageSize={pagination.pageSize}
        onPageSizeChange={onPageSizeChange}
      />

      {/**Table body */}
      <Table columnData={columns} rowData={data} />

      {/**bottom pagination controls */}
      <PaginationControls
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onPageChange={onPageChange}
      />
    </>
  );
};

export default PatientTable;
